import { taxActive } from "../court/general";
import { type AxialCoord, hexDistance } from "../hex";
import { percentOf } from "../percent";
import { enemyWithin, findHex, isOwnGround, landCount } from "../run/board";
import { isProductionTick } from "../run/cadence";
import { recruitsOf, supplyOf } from "../run/pools";
import { hostile } from "../run/sides";
import { maxHpOf, titleHeal } from "../soldier/valor";
import type { PopulationRules, RunRules } from "../types/rules";
import type { FactionId, FactionTable, HexState, MutableHex, RunState, Unit } from "../types/state";
import { isVeteran } from "../units/combat";
import { inCityReach, structureComplete, wallMax, workingCities } from "./cities";

/**
 * What the land pays: supply per hex in a working city's reach (the production step) and recruits
 * (the population's growth), with the readers the screen shares.
 */

/** What a hex in reach of a working city of its owner pays a production interval. */
function yieldInReach(rules: RunRules, tick: number, hex: HexState): number {
  const working = structureComplete(hex, tick);
  const bonus =
    working && hex.structure === "city"
      ? rules.cityProductionBonus
      : working && hex.structure === "well"
        ? rules.structureProductionBonus
        : working && hex.structure === "farm"
          ? (rules.improvements?.farmSupply ?? 0)
          : 0;
  return 1 + bonus;
}

/** A production interval's income as it reaches the pool: at `taxPercent` while the tax runs (A34). */
function taxed(state: RunState, faction: FactionId, income: number): number {
  const percent = taxActive(state, faction) ? (state.rules.court?.general.taxPercent ?? 100) : 100;
  return percentOf(income, percent);
}

/**
 * What the hex pays its owner a production interval as the land stands: 0 for nobody's and
 * razed land and for land no working city of its owner reaches. A reading for the screen;
 * `produce` pays by the same two tests.
 */
export function hexYield(state: RunState, coord: AxialCoord): number {
  const hex = findHex(state, coord);
  return hex && inCityReach(state, coord) ? yieldInReach(state.rules, state.tick, hex) : 0;
}

/**
 * The faction's land within `cityReach` of one of `cities` (its working cities), in map order:
 * the land that pays (`produce`) and grows recruits (`grow`).
 */
function landInReach(state: RunState, faction: FactionId, cities: readonly HexState[]): HexState[] {
  return state.hexes.filter(
    (hex) =>
      isOwnGround(hex, faction) &&
      cities.some((city) => hexDistance(city.coord, hex.coord) <= state.rules.cityReach),
  );
}

/** What the faction's land in reach of `cities` pays a production interval, before the tax. */
function rawIncome(state: RunState, faction: FactionId, cities: readonly HexState[]): number {
  return landInReach(state, faction, cities).reduce(
    (sum, hex) => sum + yieldInReach(state.rules, state.tick, hex),
    0,
  );
}

/** What the next production step pays into the faction's pool as the land stands, the tax in it. */
export function supplyIncome(state: RunState, faction: FactionId): number {
  return taxed(state, faction, rawIncome(state, faction, workingCities(state, faction)));
}

/**
 * Step 1: on the production interval, every owned, non-razed hex within reach of an own
 * working city pays 1 into its owner's pool, a working city `cityProductionBonus` more, a
 * working well `structureProductionBonus` more; land out of reach pays nothing (Increment
 * 3.16). On the same interval a hex regrows one wall while no hostile unit stands beside it
 * and a unit with no enemy adjacent heals `healPerInterval`.
 */
export function produce(state: RunState): RunState {
  if (!isProductionTick(state)) {
    return state;
  }
  const cities = new Map(state.factions.map((faction) => [faction, workingCities(state, faction)]));
  const { population, growth } = grow(state, cities);
  return {
    ...state,
    supply: paidSupply(state, cities),
    population,
    growth,
    hexes: regrownWalls(state),
    units: healed(state),
  };
}

/** Every kingdom's pool after a production interval: the taxed income of its land in reach. */
function paidSupply(
  state: RunState,
  cities: ReadonlyMap<FactionId, readonly HexState[]>,
): FactionTable<number> {
  const supply: Partial<Record<FactionId, number>> = { ...state.supply };
  for (const faction of state.factions) {
    const income = rawIncome(state, faction, cities.get(faction) ?? []);
    supply[faction] = supplyOf(state, faction) + taxed(state, faction, income);
  }
  return supply;
}

/** Every hex after a production interval, each a new copy: one wall more while under its height and no enemy beside it. */
function regrownWalls(state: RunState): MutableHex[] {
  return state.hexes.map((hex) => {
    const regrown: MutableHex = { ...hex };
    if (
      !hex.razed &&
      hex.walls < wallMax(hex, state.rules, state.tick) &&
      !enemyWithin(state, hex.owner, hex.coord, 1)
    ) {
      regrown.walls = hex.walls + 1;
    }
    return regrown;
  });
}

/**
 * Every unit after a production interval: with no enemy beside it, `healPerInterval` back (a
 * veteran's `veteranHeal` more, the human's title's more, V1), up to its full HP (`maxHpOf`).
 */
function healed(state: RunState): Unit[] {
  return state.units.map((unit) => {
    const max = maxHpOf(state, unit);
    if (enemyWithin(state, unit.owner, unit.coord, 1) || unit.hp >= max) {
      return unit;
    }
    const veteranHeal = isVeteran(state, unit) ? (state.rules.tactics?.veteranHeal ?? 0) : 0;
    const heal = state.rules.healPerInterval + veteranHeal + titleHeal(state, unit);
    return { ...unit, hp: Math.min(max, unit.hp + heal) };
  });
}

/**
 * What one production interval adds toward the faction's next recruit (A53): 1 per hex in
 * reach of its working cities, a working hamlet's growth on top of its hex (A54), `cityGrowth`
 * per working city, and the whole at `rallyPercent` while the faction holds under
 * `rallyHexPercent` of its largest rival's land.
 */
function growthGain(
  state: RunState,
  rules: PopulationRules,
  faction: FactionId,
  own: readonly HexState[],
  holdings: ReadonlyMap<FactionId, number>,
): number {
  const hamletGrowth = state.rules.improvements?.hamletGrowth ?? 0;
  const land = landInReach(state, faction, own).reduce(
    (sum, hex) =>
      sum +
      1 +
      (hex.structure === "hamlet" && structureComplete(hex, state.tick) ? hamletGrowth : 0),
    0,
  );
  const largestRival = Math.max(
    0,
    ...state.factions
      .filter((rival) => hostile(state.rules, rival, faction))
      .map((rival) => holdings.get(rival) ?? 0),
  );
  const rallying = (holdings.get(faction) ?? 0) * 100 < largestRival * rules.rallyHexPercent;
  const gain = land + rules.cityGrowth * own.length;
  return rallying ? percentOf(gain, rules.rallyPercent) : gain;
}

/** Every kingdom's land count, the rally's measure (A53). */
function landHoldings(state: RunState): ReadonlyMap<FactionId, number> {
  return new Map(state.factions.map((faction) => [faction, landCount(state, faction)]));
}

/**
 * What the next production step adds toward the faction's next recruit as the land stands;
 * 0 without the population block. A reading for the screen: `grow` adds the same sum.
 */
export function recruitGrowth(state: RunState, faction: FactionId): number {
  const rules = state.rules.population;
  if (!rules) {
    return 0;
  }
  return growthGain(state, rules, faction, workingCities(state, faction), landHoldings(state));
}

/**
 * A53: the land grows recruits. Each faction's growth gains 1 per hex in reach of its working
 * cities and `cityGrowth` per working city; at `perRecruit` it converts, never past `max`, and
 * growth waits at the ceiling. Without the population block both tables come back untouched.
 */
function grow(
  state: RunState,
  cities: ReadonlyMap<FactionId, readonly HexState[]>,
): { population: FactionTable<number>; growth: FactionTable<number> } {
  const rules = state.rules.population;
  if (!rules) {
    return { population: state.population, growth: state.growth };
  }
  const population: Partial<Record<FactionId, number>> = { ...state.population };
  const growth: Partial<Record<FactionId, number>> = { ...state.growth };
  const holdings = landHoldings(state);
  for (const faction of state.factions) {
    let recruits = recruitsOf(state, faction);
    let progress =
      (state.growth[faction] ?? 0) +
      growthGain(state, rules, faction, cities.get(faction) ?? [], holdings);
    while (progress >= rules.perRecruit && recruits < rules.max) {
      recruits += 1;
      progress -= rules.perRecruit;
    }
    population[faction] = recruits;
    growth[faction] = Math.min(progress, rules.perRecruit);
  }
  return { population, growth };
}
